import { useState, useEffect, useCallback, useRef } from 'react'
import { Address } from '../../../shared/types/battery'
import { Address as ApiAddress, ShippingOption } from '../../../types/api'
import { fetchAddressByCEP, formatCEP } from '../../../services/viacep'
import userService from '../../../services/userService'
import shippingService from '../../../services/shippingService'
import { useCart } from '../../cart/contexts/CartContext'

interface UseCheckoutAddressProps {
  isAuthenticated: boolean
  user: { id: string } | null
  deliveryMethod?: 'shipping' | 'pickup' | null
}

export const useCheckoutAddress = ({ isAuthenticated, user, deliveryMethod }: UseCheckoutAddressProps) => {
  const { state: cartState } = useCart()
  const [shippingAddress, setShippingAddress] = useState<Address>({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Brasil'
  })
  const [userAddresses, setUserAddresses] = useState<ApiAddress[]>([])
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false)
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)
  const [showNewAddressForm, setShowNewAddressForm] = useState(false)
  const [addressConfirmed, setAddressConfirmed] = useState(false)
  const [isLoadingCEP, setIsLoadingCEP] = useState(false)
  const [cepError, setCepError] = useState<string | null>(null)
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([])
  const [isLoadingShipping, setIsLoadingShipping] = useState(false)
  const [selectedShippingOption, setSelectedShippingOption] = useState<string | null>(null)
  const lastCalculatedZipCode = useRef<string | null>(null)

  // Carregar endereços do usuário quando autenticado
  useEffect(() => {
    const loadUserAddresses = async () => {
      if (!isAuthenticated || !user) {
        setUserAddresses([])
        setShowNewAddressForm(true)
        return
      }

      try {
        setIsLoadingAddresses(true)
        const response = await userService.getAddresses()
        const addresses = response.addresses ?? []
        setUserAddresses(addresses)

        if (addresses.length === 0) {
          setShowNewAddressForm(true)
        } else {
          const defaultAddress = addresses.find(addr => addr.isDefault)
          if (defaultAddress && defaultAddress.id) {
            setSelectedAddressId(defaultAddress.id)
            void loadAddressToForm(defaultAddress)
          }
        }
      } catch (error) {
        console.error('Erro ao carregar endereços:', error)
        setShowNewAddressForm(true)
      } finally {
        setIsLoadingAddresses(false)
      }
    }

    loadUserAddresses()
  }, [isAuthenticated, user])

  // Função para carregar endereço selecionado no formulário
  const loadAddressToForm = async (address: ApiAddress) => {
    setShippingAddress({
      street: address.street,
      number: address.number,
      complement: address.complement || '',
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country || 'Brasil'
    })
    setAddressConfirmed(true)

    // Calcular frete imediatamente quando endereço for selecionado, apenas se for entrega
    if (deliveryMethod !== 'shipping') return

    const zipCodeDigits = address.zipCode?.replace(/\D/g, '') || ''
    if (zipCodeDigits.length === 8) {
      try {
        setIsLoadingShipping(true)
        const zipCode = `${zipCodeDigits.slice(0, 5)}-${zipCodeDigits.slice(5)}`

        const items = cartState.items.map(item => ({
          batteryId: item.product.id,
          quantity: item.quantity,
          weight: item.product.weight || 0
        }))

        const response = await shippingService.calculate({
          zipCode,
          items
        })

        setShippingOptions(response.options)

        setSelectedShippingOption(prev => {
          if (!prev && response.options.length > 0) {
            return response.options[0].method
          }
          return prev
        })
      } catch (error) {
        console.error('Erro ao calcular frete:', error)
        setShippingOptions([])
        setSelectedShippingOption(null)
      } finally {
        setIsLoadingShipping(false)
      }
    }
  }

  // Função para calcular frete quando CEP for válido
  const calculateShippingOptions = useCallback(async () => {
    // Se não for entrega, não calcula frete
    if (deliveryMethod !== 'shipping') {
      setShippingOptions([])
      setSelectedShippingOption(null)
      return
    }

    const zipCodeDigits = shippingAddress.zipCode.replace(/\D/g, '')
    if (zipCodeDigits.length !== 8) {
      setShippingOptions([])
      setSelectedShippingOption(null)
      lastCalculatedZipCode.current = null
      return
    }

    if (lastCalculatedZipCode.current === zipCodeDigits) {
      return
    }

    try {
      setIsLoadingShipping(true)
      lastCalculatedZipCode.current = zipCodeDigits

      let zipCode = zipCodeDigits
      if (zipCode.length === 8) {
        zipCode = `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`
      }

      const items = cartState.items.map(item => ({
        batteryId: item.product.id,
        quantity: item.quantity,
        weight: item.product.weight || 0
      }))

      const response = await shippingService.calculate({
        zipCode,
        items
      })

      setShippingOptions(response.options)

      setSelectedShippingOption(prev => {
        if (!prev && response.options.length > 0) {
          return response.options[0].method
        }
        return prev
      })
    } catch (error) {
      console.error('Erro ao calcular frete:', error)
      setShippingOptions([])
      setSelectedShippingOption(null)
      lastCalculatedZipCode.current = null
    } finally {
      setIsLoadingShipping(false)
    }
  }, [shippingAddress.zipCode, cartState.items, deliveryMethod])

  // Calcular frete automaticamente quando CEP for válido
  useEffect(() => {
    const zipCodeDigits = shippingAddress.zipCode.replace(/\D/g, '')
    if (zipCodeDigits.length === 8) {
      const timeoutId = setTimeout(() => {
        void calculateShippingOptions()
      }, 500)

      return () => clearTimeout(timeoutId)
    } else {
      setShippingOptions([])
      setSelectedShippingOption(null)
      lastCalculatedZipCode.current = null
    }
  }, [shippingAddress.zipCode, calculateShippingOptions])

  const handleAddressChange = (field: keyof Address, value: string) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }))
  }

  const handleCEPBlur = async (cep: string) => {
    if (!cep || cep.length < 8) return

    setIsLoadingCEP(true)
    setCepError(null)

    try {
      const addressData = await fetchAddressByCEP(cep)

      if (addressData) {
        setShippingAddress(prev => ({
          ...prev,
          zipCode: formatCEP(addressData.zipCode),
          street: addressData.street,
          neighborhood: addressData.neighborhood,
          city: addressData.city,
          state: addressData.state
        }))
      }
    } catch (error) {
      setCepError(error instanceof Error ? error.message : 'Erro ao consultar CEP')
    } finally {
      setIsLoadingCEP(false)
    }
  }

  const saveNewAddress = async (): Promise<boolean> => {
    if (!isAuthenticated || !user) {
      return false
    }

    // Se já tem um endereço selecionado, não precisa salvar
    if (selectedAddressId) {
      return true
    }

    // Se está mostrando o formulário de novo endereço, salvar
    if (showNewAddressForm) {
      try {
        const response = await userService.addAddress({
          name: 'Endereço de Entrega',
          street: shippingAddress.street,
          number: shippingAddress.number,
          complement: shippingAddress.complement,
          neighborhood: shippingAddress.neighborhood,
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipCode: shippingAddress.zipCode,
          country: shippingAddress.country || 'Brasil',
          isDefault: userAddresses.length === 0
        })

        // Recarregar endereços após salvar
        const addressesResponse = await userService.getAddresses()
        const updatedAddresses = addressesResponse.addresses ?? []
        setUserAddresses(updatedAddresses)

        // Selecionar o endereço recém-criado
        if (response.address?.id) {
          setSelectedAddressId(response.address.id)
          setShowNewAddressForm(false)
        }

        return true
      } catch (error) {
        console.error('Erro ao salvar endereço:', error)
        return false
      }
    }

    return true
  }

  return {
    shippingAddress,
    setShippingAddress,
    userAddresses,
    isLoadingAddresses,
    selectedAddressId,
    setSelectedAddressId,
    showNewAddressForm,
    setShowNewAddressForm,
    addressConfirmed,
    setAddressConfirmed,
    isLoadingCEP,
    cepError,
    setCepError,
    shippingOptions,
    isLoadingShipping,
    selectedShippingOption,
    setSelectedShippingOption,
    loadAddressToForm,
    calculateShippingOptions,
    handleAddressChange,
    handleCEPBlur,
    saveNewAddress
  }
}

